import { z } from 'zod'
import type { Hex, LowerHex } from '../../types'
import { addressPrimitiveSchema, lowerHexSchema } from '../../types/primitive'
import type { LogWithBlockNumber } from '../types'
import type { BlockscoutClient } from './client'

type GetEventLogsByAddressAndTopicParameters = {
  client: BlockscoutClient
  address: Hex
  topic0: LowerHex
  fromBlock: number | 'latest'
  toBlock: number | 'latest'
  headers?: Record<string, string>
}

const logSchema = z.object({
  address: addressPrimitiveSchema,
  blockNumber: z
    .string()
    .regex(/^0x[\da-f]*$/)
    .transform((x) => (x === '0x' ? 0 : Number(x))),
  data: lowerHexSchema,
  logIndex: z
    .string()
    .regex(/^0x[\da-f]*$/)
    .transform((x) => (x === '0x' ? 0 : Number(x))),
  topics: z
    .array(lowerHexSchema.nullable())
    .transform((x) => x.filter((v): v is LowerHex => v !== null)),
  transactionHash: lowerHexSchema,
})
const resultSchema = z.array(logSchema)

/** {@link https://docs.blockscout.com/developer-support/api/rpc-endpoints/logs#get-event-logs-by-address-and-or-topic-s} */
export const getEventLogsByAddressAndTopic = async ({
  client,
  address,
  topic0,
  fromBlock,
  toBlock,
  headers,
}: GetEventLogsByAddressAndTopicParameters): Promise<LogWithBlockNumber[]> => {
  try {
    const result = await client.get({
      path: '',
      query: {
        module: 'logs',
        action: 'getLogs',
        address,
        topic0,
        fromBlock: fromBlock.toString(),
        toBlock: toBlock.toString(),
      },
      resultSchema,
      headers,
    })
    return result
  } catch (error) {
    if (error instanceof Error && error.message.includes('No logs found')) {
      return []
    }
    throw error
  }
}
